import { action, observable, makeObservable } from 'mobx';

import type { Journey } from 'Global/Journey/Types/Journey';
import { FeeEntity, GiftCertificateUsageEntity, UserEntity } from 'Models/Entities';
import { CargoCharacteristicIdentity } from 'Models/Entities/CargoCharacteristicEntity';
import * as Enums from 'Models/Enums';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { BookingAddOn } from '../../../BookingWizard/FerryTripBookingWizard/BookingWizardData/BookingAddOn';
import { PassengerDetail } from '../../../BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { CheckInBookingOverviewDto } from '../CheckInEntities/CheckInBookingOverviewDto';
import * as BookingWizardTypes from '../../../BookingWizard/FerryTripBookingWizard/BookingWizardTypes';

export interface FreightBookingDetail {
	freightTypeId: string;
	freightCategoryId: string;
	handlingIds: string[];
	quantity: number;
	weight: number;
	width: number;
	height: number;
	length: number;
	description: string;
	addOns: BookingAddOn[];
}

export interface BaseBookingCreationData {
	bookingToEdit?: string;
	/**
	 * True means changes apply to one-way using the departure of booking.
	 * False means changes apply to one-way using the return of booking.
	 * Undefined means changes apply to both ways of booking.
	 */
	departureTrip?: boolean;

	// Feature: Multi-stop - create booking
	departingJourney?: Journey;
	returningJourney?: Journey;

	/**
	 * Used in ferry filter.
	 */
	adultTickets?: PassengerDetail[];
	/**
	 * Used in ferry filter.
	 */
	childTickets?: PassengerDetail[];
	/**
	 * Used in ferry filter.
	 */
	infantTickets?: PassengerDetail[];

	passengerDTickets?: PassengerDetail[];
	passengerETickets?: PassengerDetail[];
	passengerFTickets?: PassengerDetail[];
	passengerGTickets?: PassengerDetail[];
	passengerHTickets?: PassengerDetail[];

	// Ticket Ids
	departureTicketId: string;
	returningTicketId?: string;

	// Id for user the booking is assigned to
	userId: string;

	// Additional details
	note?: string;

	// Driver information
	driverFirstName?: string;
	driverLastName?: string;
	driverPhone?: string;

	// Vehicle fields
	cargoIdentification?: string;
	hiredVehicle?: boolean;
	cargoTypeId?: string;
	cargoMake?: string;
	cargoModel?: string;
	cargoCharacteristics?: CargoCharacteristicIdentity[];
	// Inherited vehicle characteristics (from the cargo type) the customer removed for this booking.
	removedInheritedCharacteristics?: CargoCharacteristicIdentity[];
	// Store the selected length Id for vehicle/trailer
	vehicleLengthId?: string;
	vehicleWeightId?: string;
	vehicleWidthId?: string;
	vehicleExactWeight?: number | null;
	vehicleExactWeightUnit?: string | null;
	trailerLengthId?: string;
	trailerWeightId?: string;
	trailerTypeId?: string;
	trailerWidthId?: string;
	trailerExactWeight?: number | null;
	trailerExactWeightUnit?: string | null;
	trailerCargoCharacteristics?: CargoCharacteristicIdentity[];

	// Additional options
	departingTripOptions?: BookingAddOn[];
	returningTripOptions?: BookingAddOn[];

	// Freight information
	freightTypeId?: string;
	freightCategoryId?: string;
	quantity?: number;
	weight?: number;
	width?: number;
	height?: number;
	length?: number;
	handlingIds?: string[];
	description?: string;

	receiverFirstName?: string;
	receiverLastName?: string;
	receiverPhone?: string;
	receiverEmail?: string;
	receiverCompanyName?: string;
	notifyFirstName?: string;
	notifyLastName?: string;
	notifyPhone?: string;
	notifyEmail?: string;
	notifyCompanyName?: string;

	/**
	 * An optional grouped section containing information to create multiple freight bookings; processed only if provided.
	 */
	bulkFreightWizardData?: {
		items: FreightBookingDetail[];
	};

	// Primary booking contact ("name on booking") captured on the walk-on
	// BookingDetails tab. Used to set IsPrimaryContact on a passenger row.
	primaryFirstName?: string;
	primaryLastName?: string;

	// Admin-configured dynamic field values keyed by DynamicBookingFieldEntity.Id.
	// Persisted onto AlterationContext on save.
	dynamicFieldValues?: Record<string, string>;
}

export interface IBookingFormState extends BaseBookingCreationData {
	type: BookingWizardTypes.TabSelected;
	email: string;
	user: UserEntity;
	giftCertificateUsages: GiftCertificateUsageEntity[];
	transactionFees: FeeEntity[];
	existingTransactionId?: string;
	bypassSpaceCheck: boolean;
	location: Enums.locationType;
	forEftpos: boolean;
	bookingBeingEdited?: CheckInBookingOverviewDto;
}

export class BookingFormState implements IBookingFormState {
	type: BookingWizardTypes.TabSelected;

	email: string;

	cargoMake: string;

	cargoModel: string;

	user: UserEntity = new UserEntity();

	giftCertificateUsages: GiftCertificateUsageEntity[] = [];

	transactionFees: FeeEntity[] = [];

	bookingBeingEdited?: CheckInBookingOverviewDto;

	bookingToEdit?: string;

	userId: string;

	departureTicketId: string;

	departingJourney: Journey;

	departingTripOptions: BookingAddOn[] = [];

	returningTicketId: string;

	returningJourney: Journey;

	returningTripOptions: BookingAddOn[] = [];

	adultTickets: PassengerDetail[];

	childTickets: PassengerDetail[];

	infantTickets: PassengerDetail[];

	passengerDTickets: PassengerDetail[];

	passengerETickets: PassengerDetail[];

	passengerFTickets: PassengerDetail[];

	passengerGTickets: PassengerDetail[];

	passengerHTickets: PassengerDetail[];

	driverFirstName?: string;

	driverLastName?: string;

	driverPhone?: string;

	cargoIdentification?: string;

	hiredVehicle?: boolean;

	cargoTypeId?: string;

	cargoCharacteristics: CargoCharacteristicIdentity[] = [];

	// Inherited vehicle characteristics removed for this booking (Manager+ override). Persisted on save
	// and subtracted from the effective set / capacity, mirroring the booking wizard.
	removedInheritedCharacteristics: CargoCharacteristicIdentity[] = [];

	vehicleLengthId?: string;

	vehicleWeightId?: string;

	vehicleWidthId?: string;

	vehicleExactWeight?: number | null;

	vehicleExactWeightUnit?: string | null;

	trailerLengthId?: string;

	trailerTypeId?: string;

	trailerWidthId?: string;

	trailerExactWeight?: number | null;

	trailerExactWeightUnit?: string | null;

	trailerCargoCharacteristics: CargoCharacteristicIdentity[] = [];

	existingTransactionId?: string;

	bypassSpaceCheck: boolean;

	forEftpos: boolean;

	note: string;

	dynamicFieldValues: Record<string, string> = {};

	location: Enums.locationType;

	// Freight only
	quantity: number;

	description: string;

	freightCategoryId: string;

	freightTypeId: string;

	handlingIds: string[];

	length: number;

	width: number;

	height: number;

	weight: number;

	receiverFirstName: string;

	receiverLastName: string;

	receiverPhone: string;

	receiverEmail: string;

	receiverCompanyName: string;

	notifyFirstName: string;

	notifyLastName: string;

	notifyPhone: string;

	notifyEmail: string;

	notifyCompanyName: string;

	constructor(attributes?: Partial<IBookingFormState>) {
		makeObservable<BookingFormState, 'assignAttributes'>(this, {
			type: observable,
			email: observable,
			cargoMake: observable,
			cargoModel: observable,
			user: observable,
			giftCertificateUsages: observable,
			transactionFees: observable,
			bookingBeingEdited: observable,
			bookingToEdit: observable,
			userId: observable,
			departureTicketId: observable,
			departingJourney: observable,
			departingTripOptions: observable,
			returningTicketId: observable,
			returningJourney: observable,
			returningTripOptions: observable,
			adultTickets: observable,
			childTickets: observable,
			infantTickets: observable,
			passengerDTickets: observable,
			passengerETickets: observable,
			passengerFTickets: observable,
			passengerGTickets: observable,
			passengerHTickets: observable,
			driverFirstName: observable,
			driverLastName: observable,
			driverPhone: observable,
			cargoIdentification: observable,
			hiredVehicle: observable,
			cargoTypeId: observable,
			cargoCharacteristics: observable,
			removedInheritedCharacteristics: observable,
			vehicleLengthId: observable,
			vehicleWeightId: observable,
			vehicleWidthId: observable,
			vehicleExactWeight: observable,
			vehicleExactWeightUnit: observable,
			trailerLengthId: observable,
			trailerTypeId: observable,
			trailerWidthId: observable,
			trailerExactWeight: observable,
			trailerExactWeightUnit: observable,
			trailerCargoCharacteristics: observable,
			existingTransactionId: observable,
			bypassSpaceCheck: observable,
			forEftpos: observable,
			note: observable,
			dynamicFieldValues: observable,
			location: observable,
			quantity: observable,
			description: observable,
			freightCategoryId: observable,
			freightTypeId: observable,
			handlingIds: observable,
			length: observable,
			width: observable,
			height: observable,
			weight: observable,
			receiverFirstName: observable,
			receiverLastName: observable,
			receiverPhone: observable,
			receiverEmail: observable,
			receiverCompanyName: observable,
			notifyFirstName: observable,
			notifyLastName: observable,
			notifyPhone: observable,
			notifyEmail: observable,
			notifyCompanyName: observable,
			assignAttributes: action,
		});

		this.assignAttributes(attributes);
	}

	private assignAttributes(attributes?: Partial<IBookingFormState>) {
		if (attributes === undefined) {
			return;
		}
		if (isNotNullOrUndefined(attributes.type)) {
			this.type = attributes.type;
		}
		if (isNotNullOrUndefined(attributes.bookingToEdit)) {
			this.bookingToEdit = attributes.bookingToEdit;
		}
		if (isNotNullOrUndefined(attributes.userId)) {
			this.userId = attributes.userId;
		}
		if (isNotNullOrUndefined(attributes.departureTicketId)) {
			this.departureTicketId = attributes.departureTicketId;
		}
		if (isNotNullOrUndefined(attributes.departingTripOptions) && Array.isArray(attributes.departingTripOptions)) {
			this.departingTripOptions = attributes.departingTripOptions;
		}
		if (isNotNullOrUndefined(attributes.returningTicketId)) {
			this.returningTicketId = attributes.returningTicketId;
		}
		if (isNotNullOrUndefined(attributes.returningTripOptions) && Array.isArray(attributes.returningTripOptions)) {
			this.returningTripOptions = attributes.returningTripOptions;
		}
		if (isNotNullOrUndefined(attributes.adultTickets)) {
			this.adultTickets = attributes.adultTickets;
		}
		if (isNotNullOrUndefined(attributes.childTickets)) {
			this.childTickets = attributes.childTickets;
		}
		if (isNotNullOrUndefined(attributes.infantTickets)) {
			this.infantTickets = attributes.infantTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerDTickets)) {
			this.passengerDTickets = attributes.passengerDTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerETickets)) {
			this.passengerETickets = attributes.passengerETickets;
		}
		if (isNotNullOrUndefined(attributes.passengerFTickets)) {
			this.passengerFTickets = attributes.passengerFTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerGTickets)) {
			this.passengerGTickets = attributes.passengerGTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerHTickets)) {
			this.passengerHTickets = attributes.passengerHTickets;
		}
		if (isNotNullOrUndefined(attributes.driverFirstName)) {
			this.driverFirstName = attributes.driverFirstName;
		}
		if (isNotNullOrUndefined(attributes.driverLastName)) {
			this.driverLastName = attributes.driverLastName;
		}
		if (isNotNullOrUndefined(attributes.driverPhone)) {
			this.driverPhone = attributes.driverPhone;
		}
		if (isNotNullOrUndefined(attributes.cargoIdentification)) {
			this.cargoIdentification = attributes.cargoIdentification;
		}
		if (isNotNullOrUndefined(attributes.hiredVehicle)) {
			this.hiredVehicle = attributes.hiredVehicle;
		}
		if (isNotNullOrUndefined(attributes.cargoTypeId)) {
			this.cargoTypeId = attributes.cargoTypeId;
		}
		if (isNotNullOrUndefined(attributes.vehicleLengthId)) {
			this.vehicleLengthId = attributes.vehicleLengthId;
		}
		if (isNotNullOrUndefined(attributes.vehicleWeightId)) {
			this.vehicleWeightId = attributes.vehicleWeightId;
		}
		if (isNotNullOrUndefined(attributes.vehicleWidthId)) {
			this.vehicleWidthId = attributes.vehicleWidthId;
		}
		if (isNotNullOrUndefined(attributes.vehicleExactWeight)) {
			this.vehicleExactWeight = attributes.vehicleExactWeight;
		}
		if (isNotNullOrUndefined(attributes.vehicleExactWeightUnit)) {
			this.vehicleExactWeightUnit = attributes.vehicleExactWeightUnit;
		}
		if (isNotNullOrUndefined(attributes.trailerLengthId)) {
			this.trailerLengthId = attributes.trailerLengthId;
		}
		if (isNotNullOrUndefined(attributes.trailerExactWeight)) {
			this.trailerExactWeight = attributes.trailerExactWeight;
		}
		if (isNotNullOrUndefined(attributes.trailerExactWeightUnit)) {
			this.trailerExactWeightUnit = attributes.trailerExactWeightUnit;
		}
		if (isNotNullOrUndefined(attributes.trailerTypeId)) {
			this.trailerTypeId = attributes.trailerTypeId;
		}
		if (isNotNullOrUndefined(attributes.trailerWidthId)) {
			this.trailerWidthId = attributes.trailerWidthId;
		}
		if (isNotNullOrUndefined(attributes.existingTransactionId)) {
			this.existingTransactionId = attributes.existingTransactionId;
		}
		if (isNotNullOrUndefined(attributes.email)) {
			this.email = attributes.email;
		}
		if (isNotNullOrUndefined(attributes.cargoMake)) {
			this.cargoMake = attributes.cargoMake;
		}
		if (isNotNullOrUndefined(attributes.cargoModel)) {
			this.cargoModel = attributes.cargoModel;
		}
		if (isNotNullOrUndefined(attributes.cargoCharacteristics) && Array.isArray(attributes.cargoCharacteristics)) {
			this.cargoCharacteristics = attributes.cargoCharacteristics;
		}
		if (
			isNotNullOrUndefined(attributes.removedInheritedCharacteristics) &&
			Array.isArray(attributes.removedInheritedCharacteristics)
		) {
			this.removedInheritedCharacteristics = attributes.removedInheritedCharacteristics;
		}
		if (
			isNotNullOrUndefined(attributes.trailerCargoCharacteristics) &&
			Array.isArray(attributes.trailerCargoCharacteristics)
		) {
			this.trailerCargoCharacteristics = attributes.trailerCargoCharacteristics;
		}
		if (isNotNullOrUndefined(attributes.user)) {
			this.user = attributes.user;
		}
		if (isNotNullOrUndefined(attributes.bypassSpaceCheck)) {
			this.bypassSpaceCheck = attributes.bypassSpaceCheck;
		}
		if (isNotNullOrUndefined(attributes.note)) {
			this.note = attributes.note;
		}
		if (isNotNullOrUndefined(attributes.dynamicFieldValues)) {
			this.dynamicFieldValues = attributes.dynamicFieldValues;
		}
		if (attributes.departingJourney) {
			this.departingJourney = attributes.departingJourney;
		}
		if (attributes.returningJourney) {
			this.returningJourney = attributes.returningJourney;
		}
		if (isNotNullOrUndefined(attributes?.freightCategoryId)) {
			this.freightCategoryId = attributes?.freightCategoryId;
		}
		if (isNotNullOrUndefined(attributes?.freightTypeId)) {
			this.freightTypeId = attributes?.freightTypeId;
		}
		if (isNotNullOrUndefined(attributes?.handlingIds)) {
			this.handlingIds = attributes?.handlingIds;
		}
		if (isNotNullOrUndefined(attributes?.quantity)) {
			this.quantity = attributes?.quantity;
		}
		if (stringNotEmpty(attributes?.description)) {
			this.description = attributes?.description;
		}
		if (isNotNullOrUndefined(attributes?.weight)) {
			this.weight = attributes?.weight;
		}
		if (isNotNullOrUndefined(attributes?.length)) {
			this.length = attributes?.length;
		}

		// Receiver details
		if (isNotNullOrUndefined(attributes?.width)) {
			this.width = attributes?.width;
		}
		if (isNotNullOrUndefined(attributes?.height)) {
			this.height = attributes?.height;
		}
		if (isNotNullOrUndefined(attributes?.receiverFirstName)) {
			this.receiverFirstName = attributes?.receiverFirstName;
		}
		if (isNotNullOrUndefined(attributes?.receiverLastName)) {
			this.receiverLastName = attributes?.receiverLastName;
		}
		if (isNotNullOrUndefined(attributes?.receiverPhone)) {
			this.receiverPhone = attributes?.receiverPhone;
		}
		if (isNotNullOrUndefined(attributes?.receiverEmail)) {
			this.receiverEmail = attributes?.receiverEmail;
		}
		if (isNotNullOrUndefined(attributes?.receiverCompanyName)) {
			this.receiverCompanyName = attributes?.receiverCompanyName;
		}

		// Notify details
		if (isNotNullOrUndefined(attributes?.notifyFirstName)) {
			this.notifyFirstName = attributes?.notifyFirstName;
		}
		if (isNotNullOrUndefined(attributes?.notifyLastName)) {
			this.notifyLastName = attributes?.notifyLastName;
		}
		if (isNotNullOrUndefined(attributes?.notifyPhone)) {
			this.notifyPhone = attributes?.notifyPhone;
		}
		if (isNotNullOrUndefined(attributes?.notifyEmail)) {
			this.notifyEmail = attributes?.notifyEmail;
		}
		if (isNotNullOrUndefined(attributes?.notifyCompanyName)) {
			this.notifyCompanyName = attributes?.notifyCompanyName;
		}
	}

	public reset(): void {
		this.adultTickets = [];
		this.childTickets = [];
		this.infantTickets = [];
		this.passengerDTickets = [];
		this.passengerETickets = [];
		this.passengerFTickets = [];
		this.passengerGTickets = [];
		this.passengerHTickets = [];
	}
}

export function getBookingTypeFromBookingFormState(bookingFormState: IBookingFormState): Enums.bookingType {
	if (bookingFormState?.type === 'vehicle') {
		return 'VEHICLE';
	}
	if (bookingFormState?.type === 'freight') {
		return 'FREIGHT';
	}
	return 'PASSENGER_ONLY';
}

export const getTotalPassengerCountFromFormState = (formState: IBookingFormState) => {
	return (
		(formState.adultTickets?.length ?? 0) +
		(formState.childTickets?.length ?? 0) +
		(formState.infantTickets?.length ?? 0) +
		(formState.passengerDTickets?.length ?? 0) +
		(formState.passengerETickets?.length ?? 0) +
		(formState.passengerFTickets?.length ?? 0) +
		(formState.passengerGTickets?.length ?? 0) +
		(formState.passengerHTickets?.length ?? 0)
	);
};
